import * as Y from "yjs";
import { getWidget } from "@playbookai/ui";
import { metaMap, widgetsMap } from "@playbookai/collab-client";
import {
  SOLVE_PHASES,
  type SolvePhase,
  type WidgetKind,
} from "@playbookai/types";
import type { PlacementResponse } from "../api/playbook-be-client.js";
import {
  markDefaultRosterRef,
  SOLVE_WIDGET_LAYOUT_VERSION,
} from "./solve-widget-layout.js";
import { SOLVE_DEFAULT_PHASE } from "../widgets/phase-mapping.js";

/**
 * Restore missing Yjs references from persisted SOLVE placements.
 *
 * SOLVE is conversation-first: no canvas content is ever created automatically,
 * so this recovery pass is the only remaining system-owned writer of canvas
 * references. Placements are recovery evidence only: an existing Yjs reference
 * always wins, including its coordinates. Recovered references use a
 * deterministic key so repeating reconciliation cannot add another canvas node.
 */
export function reconcilePersistedWidgetPlacements(
  doc: Y.Doc,
  placements: readonly PlacementResponse[],
): number {
  const refs = widgetsMap(doc);
  const existingWidgetIds = new Set<string>();
  const existingPlacementIds = new Set<string>();
  refs.forEach((ymap) => {
    const widgetId = ymap.get("widgetId");
    const placementId = ymap.get("placementId");
    if (typeof widgetId === "string") existingWidgetIds.add(widgetId);
    if (typeof placementId === "string") existingPlacementIds.add(placementId);
  });

  const docHadNoWidgetRefs = refs.size === 0;
  let recovered = 0;
  doc.transact(() => {
    for (const placement of placements) {
      if (
        existingWidgetIds.has(placement.widget.id) ||
        existingPlacementIds.has(placement.id)
      ) {
        continue;
      }
      const def = getWidget(placement.widget.kind as WidgetKind);
      if (!def || def.storage !== "sql") continue;
      const position = placement.position;
      const x = finitePositionValue(position, "x");
      const y = finitePositionValue(position, "y");
      if (x === null || y === null) {
        console.warn(
          `[reconcilePersistedWidgetPlacements] placement ${placement.id} has no canvas position`,
        );
        continue;
      }

      const createdAt = Date.parse(placement.createdAt);
      const wmap = new Y.Map<unknown>();
      wmap.set("kind", def.kind);
      wmap.set(
        "phase",
        normalizedWidgetPhase(placement.widget.kind, placement.phase) ?? "any",
      );
      if (
        def.defaultRoster &&
        position?.["isDefaultRoster"] === true &&
        position["rosterKey"] === def.kind
      ) {
        markDefaultRosterRef(wmap, def.kind);
      }
      wmap.set("widgetId", placement.widget.id);
      wmap.set("placementId", placement.id);
      wmap.set("x", x);
      wmap.set("y", y);
      wmap.set("w", finitePositionValue(position, "w") ?? def.defaultSize.w);
      wmap.set("h", finitePositionValue(position, "h") ?? def.defaultSize.h);
      wmap.set("z", Number.isFinite(createdAt) ? createdAt : 0);
      wmap.set("createdAt", Number.isFinite(createdAt) ? createdAt : 0);
      wmap.set("createdBy", placement.createdBy);
      refs.set(placementRefKey(placement.id), wmap);
      existingWidgetIds.add(placement.widget.id);
      existingPlacementIds.add(placement.id);
      recovered += 1;
    }

    // A document that lost all canvas references also lost its layout marker.
    // Its persisted placement coordinates are the recovery authority, so mark
    // that recovered snapshot current instead of running a layout migration
    // that would immediately replace those coordinates.
    if (docHadNoWidgetRefs && recovered > 0) {
      metaMap(doc).set("solveWidgetLayoutVersion", SOLVE_WIDGET_LAYOUT_VERSION);
    }
  });
  return recovered;
}

function normalizedWidgetPhase(
  kind: string,
  phase: unknown,
): SolvePhase | null {
  if (typeof phase === "string" && phase !== "" && phase !== "any") {
    return SOLVE_PHASES.includes(phase as SolvePhase)
      ? (phase as SolvePhase)
      : null;
  }
  return SOLVE_DEFAULT_PHASE[kind as WidgetKind] ?? null;
}

function placementRefKey(placementId: string): string {
  return `placement:${placementId}`;
}

function finitePositionValue(
  position: Record<string, unknown> | null,
  key: "x" | "y" | "w" | "h",
): number | null {
  const value = position?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
